import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './HealthAssessmentReport.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileMedical, faCalendarAlt, faUser, faShieldAlt,
  faChartBar, faWeight, faSmile, faBed, faUtensils, faRunning, faBrain,
  faDownload, faClipboardCheck, faVirus, faHistory,
  faWineGlass, faSmoking, faDna, faToilet, faBriefcase,
  faLightbulb, faCheckCircle, faTint, faLeaf, faPills,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import healthAssessmentService, {
  ReportData, ReportSection, ReportMetric, ReportRecommendation,
} from '../../services/healthAssessmentService';

// =====================================================================
// PROPS
// =====================================================================

interface HealthAssessmentReportProps {
  assessmentId: number;
  onBackToAssessments: () => void;
  // Optional pre-fetched payload (from submit response) — skips /report/ call.
  reportData?: ReportData;
}

// =====================================================================
// ICON MAPS — client-side only (API returns data-only)
// =====================================================================

const SECTION_ICONS: Record<string, { icon: IconDefinition; color: string; bg: string }> = {
  'Current Health':    { icon: faVirus,     color: '#f43f5e', bg: 'rgba(244,63,94,0.08)' },
  'Medical History':   { icon: faHistory,   color: '#6366f1', bg: 'rgba(99,102,241,0.08)' },
  'Sleep Pattern':     { icon: faBed,       color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' },
  'Eating Habits':     { icon: faUtensils,  color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
  'Alcohol & Tobacco': { icon: faWineGlass, color: '#f97316', bg: 'rgba(249,115,22,0.08)' },
  'Family History':    { icon: faDna,       color: '#6366f1', bg: 'rgba(99,102,241,0.08)' },
  'Bowel & Bladder':   { icon: faToilet,    color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
  'Physical Fitness':  { icon: faRunning,   color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
  'Mental Wellness':   { icon: faBrain,     color: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },
  'Work Stress':       { icon: faBriefcase, color: '#06b6d4', bg: 'rgba(6,182,212,0.08)' },
};

const RECOMMENDATION_ICONS: Record<string, IconDefinition> = {
  'Weight Management': faWeight,
  'Nutritional Support': faWeight,
  'Improve Sleep Duration': faBed,
  'Improve Sleep Quality': faBed,
  'Reduce Junk Food': faUtensils,
  'Increase Water Intake': faTint,
  'Eat More Fruits & Vegetables': faLeaf,
  'Alcohol Consumption': faWineGlass,
  'Tobacco Use - Action Required': faSmoking,
  'Mental Health Support': faBrain,
  'Mental Wellness': faBrain,
  'Work-Life Balance': faBriefcase,
  'Start Physical Activity': faRunning,
  'Medication Compliance': faPills,
  'Healthy BMI': faCheckCircle,
  'Tobacco-Free': faCheckCircle,
  'Alcohol-Free': faCheckCircle,
};

const METRIC_ICONS: Record<string, IconDefinition> = {
  'BMI Status': faWeight,
  'Mood': faSmile,
  'Sleep Quality': faBed,
  'Diet Score': faUtensils,
  'Fitness': faRunning,
  'Mental Wellness': faBrain,
};

// =====================================================================
// SUB-COMPONENTS
// =====================================================================

const ReportHeader: React.FC<{ report: ReportData }> = ({ report }) => {
  const { t } = useTranslation('assessment');
  return (
  <div className="har-header">
    <div className="har-header-banner">
      <div className="har-header-logo">
        <FontAwesomeIcon icon={faFileMedical} className="har-header-logo-icon" />
        <span className="har-header-brand">{t('reportTitle')}</span>
      </div>
      <div className="har-header-meta">
        <span className="har-header-date">
          <FontAwesomeIcon icon={faCalendarAlt} /> {report.generated_date}
        </span>
      </div>
    </div>
    <div className="har-patient-card">
      <div className="har-patient-avatar">
        <FontAwesomeIcon icon={faUser} />
      </div>
      <div className="har-patient-info">
        <h2 className="har-patient-name">{report.display_name}</h2>
        <div className="har-patient-details">
          {report.gender_display && <span>{report.gender_display}</span>}
          {report.patient_age != null && report.patient_age > 0 && (
            <span>{t('years', { count: report.patient_age })}</span>
          )}
          {report.risk_category && (
            <span className={`har-risk-badge har-risk-${report.risk_category.toLowerCase()}`}>
              <FontAwesomeIcon icon={faShieldAlt} /> {report.risk_category}
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};

const OverallSummary: React.FC<{ report: ReportData }> = ({ report }) => {
  const { t } = useTranslation('assessment');
  return (
  <div className="har-section">
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        padding: '20px 24px', borderRadius: 16, background: report.status_bg,
        border: `1px solid ${report.status_color}33`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
        <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, color: report.status_color }}>
          {report.overall_score}<span style={{ fontSize: 18, fontWeight: 600 }}>/100</span>
        </div>
        <span
          style={{
            marginTop: 8, padding: '3px 12px', borderRadius: 999,
            background: report.status_color, color: '#fff', fontSize: 12, fontWeight: 700,
          }}
        >
          {report.status_label}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
          {t('overallSummary')}
        </h3>
        <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
          {report.narrative}
        </p>
        {report.risk_category && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>
            {t('riskCategory')} <strong style={{ color: report.status_color }}>{report.risk_category}</strong>
          </p>
        )}
        {report.focus_areas.length > 0 && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>
            {t('focusAreas')} <strong>{report.focus_areas.join(', ')}</strong>
          </p>
        )}
      </div>
    </div>
  </div>
  );
};

const MetricCard: React.FC<{ metric: ReportMetric }> = ({ metric }) => {
  const icon = METRIC_ICONS[metric.label] || faChartBar;
  return (
    <div className="har-metric-card">
      <div className="har-metric-icon" style={{ background: metric.bg_color, color: metric.color }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="har-metric-label">{metric.label}</div>
      <div className="har-metric-value" style={{ color: metric.color }}>{metric.value}</div>
      {metric.score !== null && metric.score !== undefined && (
        <div className="har-metric-bar-track">
          <div className="har-metric-bar-fill" style={{ width: `${metric.score}%`, background: metric.color }} />
        </div>
      )}
    </div>
  );
};

const OverviewDashboard: React.FC<{ report: ReportData }> = ({ report }) => {
  const { t } = useTranslation('assessment');
  return (
  <div className="har-section">
    <div className="har-section-header">
      <FontAwesomeIcon icon={faChartBar} className="har-section-icon" />
      <h3 className="har-section-title">{t('healthOverview')}</h3>
    </div>
    <div className="har-overview-grid">
      {report.metrics.map((m, i) => <MetricCard key={i} metric={m} />)}
    </div>
  </div>
  );
};

const BodyVitals: React.FC<{ report: ReportData }> = ({ report }) => {
  const { t } = useTranslation('assessment');
  const bmiMarkerPos = report.bmi_numeric != null
    ? Math.min(100, Math.max(0, ((report.bmi_numeric - 10) / 30) * 100))
    : 50;

  return (
    <div className="har-section">
      <div className="har-section-header">
        <FontAwesomeIcon icon={faWeight} className="har-section-icon" />
        <h3 className="har-section-title">{t('bodyVitals')}</h3>
      </div>
      <div className="har-vitals-row">
        <div className="har-vital-item">
          <span className="har-vital-label">{t('height')}</span>
          <span className="har-vital-value">{report.height_display}</span>
        </div>
        <div className="har-vital-item">
          <span className="har-vital-label">{t('weight')}</span>
          <span className="har-vital-value">{report.weight_display}</span>
        </div>
        <div className="har-vital-item">
          <span className="har-vital-label">{t('bmi')}</span>
          <span className="har-vital-value" style={{ color: report.bmi_color }}>
            {report.bmi_numeric ?? '---'}
          </span>
        </div>
        <div className="har-vital-item">
          <span className="har-vital-label">{t('healthOpinion')}</span>
          <span className="har-vital-value" style={{ color: report.health_opinion_color }}>
            {report.health_opinion_display}
          </span>
        </div>
      </div>
      <div className="har-bmi-gauge">
        <div className="har-bmi-scale">
          <div className="har-bmi-segment har-bmi-under" style={{ flex: 1.5 }}>{t('bmiUnderweight')}<br />&lt;18.5</div>
          <div className="har-bmi-segment har-bmi-normal" style={{ flex: 2 }}>{t('bmiNormal')}<br />18.5-24.9</div>
          <div className="har-bmi-segment har-bmi-over" style={{ flex: 2 }}>{t('bmiOverweight')}<br />25-29.9</div>
          <div className="har-bmi-segment har-bmi-obese" style={{ flex: 2.5 }}>{t('bmiObese')}<br />&gt;30</div>
        </div>
        {report.bmi_numeric != null && (
          <div className="har-bmi-marker-track">
            <div className="har-bmi-marker" style={{ left: `${bmiMarkerPos}%` }}>
              <div className="har-bmi-marker-pin" />
              <span className="har-bmi-marker-label" style={{ color: report.bmi_color }}>
                {report.bmi_numeric}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LifestyleSection: React.FC<{ section: ReportSection }> = ({ section }) => {
  const icons = SECTION_ICONS[section.title] || { icon: faChartBar, color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' };
  return (
    <div className="har-ls-section">
      <div className="har-ls-header">
        <div className="har-ls-icon" style={{ background: icons.bg, color: icons.color }}>
          <FontAwesomeIcon icon={icons.icon} />
        </div>
        <div className="har-ls-title-group">
          <h4 className="har-ls-title">{section.title}</h4>
          <span className="har-ls-badge" style={{ color: section.status_color, background: section.status_bg }}>
            {section.status_label}
          </span>
        </div>
      </div>
      <div className="har-ls-items">
        {section.items.map((item, i) => (
          <div key={i} className={`har-ls-item har-ls-item--${item.status}`}>
            <span className="har-ls-item-label">{item.label}</span>
            <span className="har-ls-item-value">{item.value}</span>
            <span className={`har-ls-item-dot har-ls-dot--${item.status}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

// Chunks the flat sections list into rows of 2 for the existing grid layout.
const SectionGrid: React.FC<{ sections: ReportSection[] }> = ({ sections }) => {
  const rows: ReportSection[][] = [];
  for (let i = 0; i < sections.length; i += 2) rows.push(sections.slice(i, i + 2));
  return (
    <div className="har-section-grid">
      {rows.map((row, ri) => (
        <div key={ri} className="har-grid-row">
          {row.map((s, i) => <LifestyleSection key={i} section={s} />)}
        </div>
      ))}
    </div>
  );
};

const RecommendationsSection: React.FC<{ items: ReportRecommendation[] }> = ({ items }) => {
  const { t } = useTranslation('assessment');
  return (
  <div className="har-recommendations">
    <div className="har-section-header">
      <FontAwesomeIcon icon={faLightbulb} className="har-section-icon" />
      <h3 className="har-section-title">{t('recommendations')}</h3>
    </div>
    <div className="har-rec-grid">
      {items.map((rec, i) => {
        const icon = RECOMMENDATION_ICONS[rec.title] || faLightbulb;
        return (
          <div key={i} className={`har-rec-card har-rec--${rec.priority}`}>
            <div className="har-rec-icon" style={{ color: rec.icon_color }}>
              <FontAwesomeIcon icon={icon} />
            </div>
            <div className="har-rec-content">
              <span className="har-rec-title">{rec.title}</span>
              <p className="har-rec-text">{rec.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
  );
};

const ReportFooter: React.FC<{ report: ReportData }> = ({ report }) => {
  const { t } = useTranslation('assessment');
  return (
  <div className="har-footer">
    <div className="har-footer-brand">
      <FontAwesomeIcon icon={faFileMedical} className="har-footer-icon" />
      <span>{t('reportNumber', { id: report.assessment_id })}</span>
    </div>
    <p className="har-footer-disclaimer">
      {t('disclaimer')}
    </p>
    <p className="har-footer-date">{t('generatedOn', { date: report.generated_date })}</p>
  </div>
  );
};

// =====================================================================
// MAIN COMPONENT
// =====================================================================

const HealthAssessmentReport: React.FC<HealthAssessmentReportProps> = ({
  assessmentId, onBackToAssessments, reportData,
}) => {
  const { t } = useTranslation('assessment');
  const reportRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [report, setReport] = useState<ReportData | null>(reportData ?? null);
  const [loading, setLoading] = useState(!reportData);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (reportData) {
      setReport(reportData);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await healthAssessmentService.getReport(assessmentId);
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error('Failed to load report:', err);
        if (!cancelled) setLoadError(t('loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId, reportData]);

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const blob = await healthAssessmentService.downloadReport(assessmentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `health-report-${assessmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      setToastMsg(t('downloadFailed'));
      setTimeout(() => setToastMsg(null), 4000);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="har-wrapper" style={{ padding: 48, textAlign: 'center' }}>
        <p style={{ color: '#64748b' }}>{t('loading')}</p>
      </div>
    );
  }

  if (loadError || !report) {
    return (
      <div className="har-wrapper" style={{ padding: 48, textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: 16 }}>{loadError || t('unavailable')}</p>
        <button onClick={onBackToAssessments} className="har-btn-back">
          <FontAwesomeIcon icon={faClipboardCheck} /> {t('backToAssessments')}
        </button>
      </div>
    );
  }

  return (
    <div className="har-wrapper">
      <div className="har-action-bar">
        <button onClick={onBackToAssessments} className="har-btn-back">
          <FontAwesomeIcon icon={faClipboardCheck} />
          {t('backToAssessments')}
        </button>
        <button onClick={handleDownloadPdf} disabled={isDownloading} className="har-btn-download">
          <FontAwesomeIcon icon={faDownload} />
          {isDownloading ? t('generatingPdf') : t('downloadPdf')}
        </button>
      </div>
      <div className="har-report" ref={reportRef}>
        <ReportHeader report={report} />
        <OverallSummary report={report} />
        <OverviewDashboard report={report} />
        <BodyVitals report={report} />
        <SectionGrid sections={report.sections} />
        {report.recommendations.length > 0 && (
          <RecommendationsSection items={report.recommendations} />
        )}
        <ReportFooter report={report} />
      </div>

      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            background: '#ef4444',
          }}
        >
          <span>{toastMsg}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default HealthAssessmentReport;
